import { colors } from '@apollo/brand';
import { customColors, customColorsRaw } from '@apollo/orbit';
import { useMemo } from 'react';

import {
  HistogramData,
  HistogramStackConfig,
} from 'src/components/charts/helpers/useQueryHistogramStackInfo';
import { useStackInfo } from 'src/components/charts/helpers/useStackInfo';
import { usePrepareHistogramData } from 'src/components/charts/Histogram';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

export function useServiceReportHistograms(
  requestsChartData: GraphQLTypes.UI__ServiceReportQuery_service_stats_queryStats[],
  eventsChartData: GraphQLTypes.UI__ServiceReportQuery_service_stats_queryStats[],
  isSubscriptionOperation: boolean,
) {
  const requestsHistogramStacks = useMemo(
    (): HistogramStackConfig<HistogramData>[] => [
      {
        data: requestsChartData,
        primaryColor: `rgb(${customColorsRaw.flowchart.d} / 0.6)`,
        cursor: customColors.flowchart.d,
        marker: `rgb(${customColorsRaw.flowchart.c} / 0.6)`,
        label: isSubscriptionOperation ? 'Registrations' : 'Requests',
        hasData: ({ metrics }) =>
          metrics.uncachedHistogram.sparseBuckets.some((v) => v > 0),
        getValue: ({ metrics }) => metrics.uncachedHistogram,
      },
      {
        data: requestsChartData,
        primaryColor: `${colors.primitives.yellow[300]}99`,
        cursor: colors.primitives.yellow[300],
        marker: colors.primitives.yellow[300],
        label: 'Cache hits',
        hasData: ({ metrics }) =>
          metrics.cachedHistogram.sparseBuckets.some((v) => v > 0),
        getValue: ({ metrics }) => metrics.cachedHistogram,
      },
    ],
    [isSubscriptionOperation, requestsChartData],
  );

  const eventsHistogramStacks = useMemo(
    (): HistogramStackConfig<HistogramData>[] => [
      {
        data: eventsChartData,
        primaryColor: `rgba(${customColorsRaw.border.beta} / 0.6)`,
        cursor: customColors.border.beta,
        marker: customColors.border.beta,
        label: 'Notifications',
        hasData: ({ metrics }) =>
          metrics.uncachedHistogram.sparseBuckets.some((v) => v > 0),
        getValue: ({ metrics }) => metrics.uncachedHistogram,
      },
      {
        data: eventsChartData,
        primaryColor: `${colors.primitives.yellow[300]}99`,
        cursor: colors.primitives.yellow[300],
        marker: colors.primitives.yellow[300],
        label: 'Cache hits',
        hasData: ({ metrics }) =>
          metrics.cachedHistogram.sparseBuckets.some((v) => v > 0),
        getValue: ({ metrics }) => metrics.cachedHistogram,
      },
    ],
    [eventsChartData],
  );

  const requestsHistogramStackInfo = useStackInfo<
    HistogramData,
    { sparseBuckets: number[] },
    HistogramStackConfig<HistogramData>
  >(requestsHistogramStacks);
  const requestsHistogramData = usePrepareHistogramData<HistogramData>(
    requestsHistogramStackInfo,
  );

  const eventsHistogramStackInfo = useStackInfo<
    HistogramData,
    { sparseBuckets: number[] },
    HistogramStackConfig<HistogramData>
  >(eventsHistogramStacks);

  const eventsHistogramData = usePrepareHistogramData<HistogramData>(
    eventsHistogramStackInfo,
  );

  return {
    requestsHistogramStackInfo,
    eventsHistogramStackInfo,
    requestsHistogramData,
    eventsHistogramData,
  };
}
